/**
 * The one date range every view selects (ADR 0109): Today, 7 days, 30 days, 90 days or a custom
 * span of whole local days, kept in the URL (`?range=7d`, `?range=custom&from=2026-10-01&to=2026-10-14`).
 */
import { formatDay } from './format.ts';

export type DateRangePreset = 'today' | '7d' | '30d' | '90d' | 'custom';
export const DATE_RANGE_PRESETS: readonly DateRangePreset[] = ['today', '7d', '30d', '90d', 'custom'];
export const DATE_RANGE_LABEL: Record<DateRangePreset, string> = { today: 'Today', '7d': '7d', '30d': '30d', '90d': '90d', custom: 'Custom' };

export interface DateRange {
  preset: DateRangePreset;
  /** Start, epoch ms (local midnight). */
  from: number;
  /** End, epoch ms, exclusive (now for a preset, the midnight after the last day for custom). */
  to: number;
}

const DAY = 86_400_000;
const pad = (n: number) => String(n).padStart(2, '0');
/** Local midnight of the day holding t. */
export const startOfDay = (t: number): number => {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};
export const addDays = (t: number, n: number): number => {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n).getTime();
};
/** YYYY-MM-DD of the local day holding t. */
export const dayKey = (t: number): string => {
  const d = new Date(t);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
export const parseDayKey = (s: string | null | undefined): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? '');
  if (!m) return null;
  const t = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  return Number.isFinite(t) ? t : null;
};

export const isDateRangePreset = (x: unknown): x is DateRangePreset => typeof x === 'string' && (DATE_RANGE_PRESETS as readonly string[]).includes(x);

/** The span of a preset as of now. */
export function presetRange(preset: Exclude<DateRangePreset, 'custom'>, now: number): DateRange {
  const today = startOfDay(now);
  const days = preset === 'today' ? 1 : Number.parseInt(preset, 10);
  return { preset, from: addDays(today, 1 - days), to: now };
}

/** A custom span of whole days, first and last day included; swapped when given backwards. */
export function customRange(first: number, last: number): DateRange {
  const a = startOfDay(Math.min(first, last));
  const b = startOfDay(Math.max(first, last));
  return { preset: 'custom', from: a, to: addDays(b, 1) };
}

/** The range a URL asks for; `fallback` when it asks for none or something unreadable. */
export function parseDateRange(params: { get(k: string): string | null }, now: number, fallback: Exclude<DateRangePreset, 'custom'> = '7d'): DateRange {
  const raw = params.get('range');
  if (raw === 'custom') {
    const a = parseDayKey(params.get('from'));
    const b = parseDayKey(params.get('to'));
    if (a != null && b != null) return customRange(a, b);
  }
  return presetRange(isDateRangePreset(raw) && raw !== 'custom' ? raw : fallback, now);
}

/** The URL params for a range, onto a copy of `params` (the fallback preset is left out). */
export function writeDateRange(r: DateRange, params: URLSearchParams, fallback: DateRangePreset = '7d'): URLSearchParams {
  const n = new URLSearchParams(params);
  n.delete('from');
  n.delete('to');
  if (r.preset === fallback) n.delete('range');
  else n.set('range', r.preset);
  if (r.preset === 'custom') {
    n.set('from', dayKey(r.from));
    n.set('to', dayKey(r.to - 1));
  }
  return n;
}

const short = (t: number, year: boolean) => formatDay(t, year);

/** "Today", "Oct 18 to Oct 24", "Dec 28, 2025 to Jan 3, 2026": what the page header says. */
export function formatDateRange(r: DateRange, now: number = Date.now()): string {
  const last = r.preset === 'custom' ? r.to - 1 : Math.min(r.to, now);
  const thisYear = new Date(now).getFullYear();
  if (startOfDay(r.from) === startOfDay(last)) return startOfDay(last) === startOfDay(now) ? 'Today' : short(r.from, new Date(r.from).getFullYear() !== thisYear);
  const y = new Date(r.from).getFullYear() !== new Date(last).getFullYear() || new Date(last).getFullYear() !== thisYear;
  return `${short(r.from, y)} to ${short(last, y)}`;
}

/** Whole days the range covers. */
export const rangeDays = (r: DateRange): number => Math.max(1, Math.round((startOfDay(r.to - 1) - startOfDay(r.from)) / DAY) + 1);

/** Hours for a range of up to 2 days, days beyond. */
export const rangeBucket = (r: DateRange): 'hour' | 'day' => (rangeDays(r) <= 2 ? 'hour' : 'day');

/** The weeks of a month for a calendar: 6 rows of 7 local midnights, Monday first. */
export function monthGrid(month: number): number[][] {
  const d = new Date(month);
  const first = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
  const dow = (new Date(first).getDay() + 6) % 7;
  const start = addDays(first, -dow);
  return Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, i) => addDays(start, w * 7 + i)));
}
